import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { TrashIcon, PlusIcon, QuestionMarkCircleIcon, CheckIcon } from '@heroicons/react/solid'
import useQuery from '@/hooks/useQuery'
import { useGlobalState } from '@/state'
import DefaultButton from '@/shared/Buttons/Default'

export const BUCKET_COLORS = [
  { name: 'gray', bg: 'bg-gray-200 dark:bg-gray-600', text: 'text-gray-800 dark:text-gray-100', dot: 'bg-gray-400' },
  { name: 'red', bg: 'bg-red-100 dark:bg-red-900', text: 'text-red-800 dark:text-red-100', dot: 'bg-red-500' },
  { name: 'orange', bg: 'bg-orange-100 dark:bg-orange-900', text: 'text-orange-800 dark:text-orange-100', dot: 'bg-orange-500' },
  { name: 'yellow', bg: 'bg-yellow-100 dark:bg-yellow-900', text: 'text-yellow-800 dark:text-yellow-100', dot: 'bg-yellow-500' },
  { name: 'green', bg: 'bg-green-100 dark:bg-green-900', text: 'text-green-800 dark:text-green-100', dot: 'bg-green-500' },
  { name: 'teal', bg: 'bg-teal-100 dark:bg-teal-900', text: 'text-teal-800 dark:text-teal-100', dot: 'bg-teal-500' },
  { name: 'blue', bg: 'bg-blue-100 dark:bg-blue-900', text: 'text-blue-800 dark:text-blue-100', dot: 'bg-blue-500' },
  { name: 'indigo', bg: 'bg-indigo-100 dark:bg-indigo-900', text: 'text-indigo-800 dark:text-indigo-100', dot: 'bg-indigo-500' },
  { name: 'purple', bg: 'bg-purple-100 dark:bg-purple-900', text: 'text-purple-800 dark:text-purple-100', dot: 'bg-purple-500' },
  { name: 'pink', bg: 'bg-pink-100 dark:bg-pink-900', text: 'text-pink-800 dark:text-pink-100', dot: 'bg-pink-500' }
]

export const colorClasses = (color) => BUCKET_COLORS.find((c) => c.name === color) || BUCKET_COLORS[0]

const ColorSwatches = ({ value, onChange }) => (
  <div className='flex flex-wrap gap-2 sm:gap-1.5' role='radiogroup' aria-label='Bucket color'>
    {BUCKET_COLORS.map((c) => {
      const selected = value === c.name
      return (
        <button
          type='button'
          key={c.name}
          onClick={() => onChange(c.name)}
          aria-label={c.name}
          aria-checked={selected}
          role='radio'
          title={c.name}
          className={`relative w-9 h-9 sm:w-7 sm:h-7 rounded-full ${c.dot} ring-1 ring-black/10 dark:ring-white/20 cursor-pointer touch-manipulation focus:outline-none active:opacity-80 ${selected ? 'ring-2 ring-offset-2 ring-cccblue dark:ring-offset-gray-800' : ''}`}
        >
          {selected && <CheckIcon className='absolute inset-0 m-auto h-4 w-4 sm:h-3.5 sm:w-3.5 text-white drop-shadow' />}
        </button>
      )
    })}
  </div>
)

ColorSwatches.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired
}

const BucketRow = ({ bucket, projectId, onUpdate, onDelete }) => {
  const [name, setName] = useState(bucket.name)
  const [color, setColor] = useState(bucket.color)
  const [saving, setSaving] = useState(false)
  const { putpostRequest } = useQuery()
  const [, setToast] = useGlobalState('toast')
  const dirty = name !== bucket.name || color !== bucket.color

  const save = () => {
    if (!dirty || !name.trim()) { return }
    setSaving(true)
    putpostRequest(`/api/v3/manage/projects/${projectId}/buckets/${bucket.id}`, 'PATCH', { bucket: { name: name.trim(), color } }, (err, jsonData) => {
      setSaving(false)
      if (err) { return }
      onUpdate(jsonData.bucket)
    })
  }

  const destroy = () => {
    if (!window.confirm(`Delete bucket "${bucket.name}"? Submissions in this bucket will be uncategorized.`)) { return }
    putpostRequest(`/api/v3/manage/projects/${projectId}/buckets/${bucket.id}`, 'DELETE', {}, (err) => {
      if (err) { return }
      onDelete(bucket.id)
      setToast(<div className="ml-3 w-0 flex-1 pt-0.5">
        <p className="text-sm font-medium dark:text-white text-gray-800">Bucket deleted.</p>
      </div>)
    })
  }

  const swatch = colorClasses(color)
  return <div className='p-3 rounded-md bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 space-y-2'>
    <div className='flex items-center gap-2'>
      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${swatch.bg} ${swatch.text}`}>
        {name.trim() || 'Untitled'}
      </span>
      <input
        type='text'
        value={name}
        onChange={(e) => setName(e.target.value)}
        onBlur={save}
        maxLength={50}
        className='flex-grow min-w-0 text-sm py-1 px-2 rounded border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100'
      />
      {dirty && <DefaultButton onClick={save} text={saving ? 'Saving…' : 'Save'} />}
      <button type='button' onClick={destroy} aria-label='Delete bucket' className='flex-shrink-0 text-red-500 hover:text-red-700 p-1'>
        <TrashIcon className='h-5 w-5' />
      </button>
    </div>
    <ColorSwatches value={color} onChange={setColor} />
  </div>
}

BucketRow.propTypes = {
  bucket: PropTypes.object.isRequired,
  projectId: PropTypes.number.isRequired,
  onUpdate: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired
}

const NewBucketForm = ({ projectId, onCreate }) => {
  const [name, setName] = useState('')
  const [color, setColor] = useState('gray')
  const [saving, setSaving] = useState(false)
  const { putpostRequest } = useQuery()

  const create = () => {
    if (!name.trim()) { return }
    setSaving(true)
    putpostRequest(`/api/v3/manage/projects/${projectId}/buckets`, 'POST', { bucket: { name: name.trim(), color } }, (err, jsonData) => {
      setSaving(false)
      if (err) { return }
      onCreate(jsonData.bucket)
      setName('')
      setColor('gray')
    })
  }

  const swatch = colorClasses(color)
  return <div className='p-3 rounded-md border-2 border-dashed border-gray-300 dark:border-gray-600 space-y-2'>
    <div className='flex items-center gap-2'>
      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${swatch.bg} ${swatch.text}`}>
        {name.trim() || 'Preview'}
      </span>
      <input
        type='text'
        value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') { create() } }}
        placeholder='New bucket name (e.g. Maybe, Top picks)'
        maxLength={50}
        className='flex-grow min-w-0 text-sm py-1 px-2 rounded border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100'
      />
      <DefaultButton
        onClick={create}
        disabled={saving || !name.trim()}
        text={
          <div className='flex items-center space-x-1'>
            <PlusIcon className='h-4 w-4' />
            <span>Add</span>
          </div>
        }
      />
    </div>
    <ColorSwatches value={color} onChange={setColor} />
  </div>
}

NewBucketForm.propTypes = {
  projectId: PropTypes.number.isRequired,
  onCreate: PropTypes.func.isRequired
}

const BucketsManager = ({ projectId, buckets, setBuckets, onShowIntro }) => {
  const onCreate = (bucket) => setBuckets([...buckets, bucket])
  const onUpdate = (bucket) => setBuckets(buckets.map((b) => b.id === bucket.id ? bucket : b))
  const onDelete = (id) => setBuckets(buckets.filter((b) => b.id !== id))

  return <div className='space-y-2'>
    <p className='text-sm text-gray-700 dark:text-gray-200'>
      Buckets help you organize submissions with custom labels (e.g. "Maybe", "Top picks", "Audio issues"). They're separate from the dismissed/shortlisted/assigned status — only you can see them.
      {onShowIntro && (
        <>
          {' '}
          <button
            type='button'
            onClick={onShowIntro}
            className='inline-flex items-center gap-1 text-xs text-cccblue hover:text-cccblue-alt hover:underline align-baseline'
          >
            <QuestionMarkCircleIcon className='h-4 w-4' />
            What’s this?
          </button>
        </>
      )}
    </p>
    {buckets.length === 0 && (
      <p className='text-sm italic text-gray-500 dark:text-gray-400'>No buckets yet. Create one below.</p>
    )}
    {buckets.map((bucket) => (
      <BucketRow key={`bucket${bucket.id}`} bucket={bucket} projectId={projectId} onUpdate={onUpdate} onDelete={onDelete} />
    ))}
    <NewBucketForm projectId={projectId} onCreate={onCreate} />
  </div>
}

BucketsManager.propTypes = {
  projectId: PropTypes.number.isRequired,
  buckets: PropTypes.array.isRequired,
  setBuckets: PropTypes.func.isRequired,
  onShowIntro: PropTypes.func
}

export default BucketsManager
